// Track if gtag has been fully initialized
let gtagInitialized = false;

// Utility to inject Google Analytics gtag.js
// Initialize GA script on page load with consent denied
export function initializeGtag(trackingId: string) {
  // Always set up dataLayer and gtag function first
  window.dataLayer = window.dataLayer || [];

  // Must use 'arguments' object (not spread args) for gtag.js compatibility
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  // Only initialize once (consent, js, config)
  if (gtagInitialized) {
    return;
  }
  gtagInitialized = true;

  // Set default consent to denied FIRST (GDPR compliant)
  // This must happen before any other gtag calls
  window.gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied'
  });

  window.gtag('js', new Date());
  window.gtag('config', trackingId);

  // Only create script if it doesn't exist
  if (!document.getElementById('gtag-js')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    script.id = 'gtag-js';
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
    };
    document.head.appendChild(script);
  }
}

// Track if consent has been updated
let consentUpdated = false;

// Update consent after user accepts cookies
export function enableAnalytics(trackingId: string) {
  // Prevent duplicate consent updates
  if (consentUpdated) return;

  const updateConsent = () => {
    if (consentUpdated || !window.gtag) return;
    consentUpdated = true;

    window.gtag('consent', 'update', {
      'analytics_storage': 'granted',
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted'
    });

    // Re-issue config to ensure session starts
    window.gtag('config', trackingId, {
      page_path: window.location.pathname + window.location.search,
      send_page_view: true
    });

    // Send first page view event explicitly
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title
    });
  };

  const script = document.getElementById('gtag-js');

  // If script already loaded, update immediately
  if (script?.hasAttribute('data-loaded')) {
    updateConsent();
    return;
  }

  // Wait for script to load
  if (script) {
    script.addEventListener('load', updateConsent, { once: true });
  }

  // Fallback: try after short delay (handles cached scripts)
  setTimeout(updateConsent, 500);
}

// Legacy function for backwards compatibility
export function loadGtag(trackingId: string) {
  initializeGtag(trackingId);
  enableAnalytics(trackingId);
}

// Log page view
export const pageview = (url: string, trackingId: string) => {
  if (window.gtag) {
    window.gtag('config', trackingId, {
      page_path: url,
    });
  }
};

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
